import React from "react"

import i18n from "../../translations/i18n"

/**
 * The application had NO error boundary at all. React's default for an
 * uncaught render error is to unmount the whole tree, so any single component
 * throwing left a reviewer staring at a blank white page mid-review, with no
 * message, no way back, and nothing in the UI to report.
 *
 * Deliberately kept primitive:
 *
 * - No hooks — a boundary has to be a class, there is no hook equivalent.
 * - No theme, no MUI, no styled components. Whatever just threw may well be the
 *   theme or a provider, and a fallback that depends on the broken half of the
 *   app is a fallback that throws while rendering the error. Inline styles with
 *   literal colours are the only thing guaranteed to paint.
 * - i18n is read through a try/catch for the same reason: translated if the
 *   instance is healthy, English if it is not.
 *
 * "Reload" rather than "dismiss": the store and the record the reviewer was
 * working on are in an unknown state after a throw, and quietly re-rendering
 * over the top of that would be worse than starting clean. Anything they had
 * typed was already flushed by the autosave in <ModernDispoCard/>.
 */

type ErrorBoundaryProps = {
    children: React.ReactNode
}

type ErrorBoundaryState = {
    failed: boolean
}

function translate(key: string, fallback: string): string {
    try {
        const value = i18n.t(key);

        // i18next echoes the key back when it has no entry for it
        return typeof value === "string" && value !== key ? value : fallback
    } catch (error) {
        return fallback
    }
}

const shell: React.CSSProperties = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background: "#EEF2F7",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    color: "#16233A",
};

const card: React.CSSProperties = {
    maxWidth: 420,
    width: "100%",
    background: "#FFFFFF",
    border: "1px solid #DBE3ED",
    borderRadius: 12,
    padding: "26px 26px 22px",
    textAlign: "center",
};

const button: React.CSSProperties = {
    marginTop: 18,
    height: 40,
    padding: "0 20px",
    border: "1px solid #1E5EF3",
    borderRadius: 9,
    background: "#1E5EF3",
    color: "#FFFFFF",
    font: "600 13px system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    textTransform: "uppercase",
    cursor: "pointer",
};

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {failed: false};

    static getDerivedStateFromError(): ErrorBoundaryState {
        return {failed: true}
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // The only record of what happened — there is no error reporting service
        // wired up in this app, so the console is where a support session looks.
        // eslint-disable-next-line no-console
        console.error("Unhandled render error", error, info.componentStack)
    }

    handleReload = () => {
        window.location.reload()
    };

    render() {
        if (false === this.state.failed) {
            return this.props.children
        }

        return (
            <div style={shell}>
                <div style={card} role="alert">
                    <h1 style={{margin: 0, fontSize: 18, letterSpacing: "-.01em"}}>
                        {translate("error_boundary.title", "Something went wrong")}
                    </h1>
                    <p style={{margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#56677F"}}>
                        {translate(
                          "error_boundary.body",
                          "The page could not be displayed. Reloading usually clears it. Saved conclusions and parameters are not affected.",
                        )}
                    </p>
                    <button type="button" style={button} onClick={this.handleReload}>
                        {translate("error_boundary.reload", "Reload")}
                    </button>
                </div>
            </div>
        )
    }
}
